import { useSuggestAddressQuery } from '../../../store/api/dadata-api'
import { AutoComplete, Checkbox, Flex, Form } from 'antd'
import { Rule } from 'antd/es/form'
import { FC, MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react'
import { NamePath } from 'antd/es/form/interface'
import { Suggestion } from '../../../types/api/dadata/suggest-adress'

interface AddressControlProps {
    value?: string
    onChange?: (value: string | undefined) => void
    placeholder?: string
    disabled?: boolean
    onSelectSuggestion?: (suggestion: Suggestion) => void
    onClearSuggestion?: () => void
    confirmedRef: MutableRefObject<string | null>
}

const AddressControl: FC<AddressControlProps> = ({
    value,
    onChange,
    placeholder,
    disabled,
    onSelectSuggestion,
    onClearSuggestion,
    confirmedRef,
}) => {
    const [search, setSearch] = useState('')
    const [debounced, setDebounced] = useState('')
    const initializedRef = useRef(false)

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 500)
        return () => clearTimeout(t)
    }, [search])

    useEffect(() => {
        if (initializedRef.current) return
        if (typeof value === 'string' && value.trim()) {
            confirmedRef.current = value
        }
        initializedRef.current = true
    }, [value, confirmedRef])

    const { data, isFetching } = useSuggestAddressQuery(
        { query: debounced, count: 10 },
        { skip: !debounced || disabled }
    )

    const options =
        data?.suggestions.map(suggestion => ({
            label: suggestion.value,
            value: suggestion.value,
            suggestion,
        })) ?? []

    const clearConfirmed = () => {
        confirmedRef.current = null
        onClearSuggestion?.()
    }

    return (
        <AutoComplete
            value={value}
            options={options}
            onSearch={setSearch}
            onSelect={(selected, option) => {
                const next = String(selected)
                confirmedRef.current = next
                onChange?.(next)
                const suggestion = (option as { suggestion?: Suggestion }).suggestion
                if (suggestion) onSelectSuggestion?.(suggestion)
            }}
            onChange={nextValue => {
                const next = nextValue == null || nextValue === '' ? undefined : String(nextValue)
                if (!next) {
                    if (confirmedRef.current !== null) {
                        clearConfirmed()
                    }
                    onChange?.(undefined)
                    return
                }
                if (confirmedRef.current !== null && next !== confirmedRef.current) {
                    clearConfirmed()
                }
                onChange?.(next)
            }}
            filterOption={false}
            placeholder={placeholder}
            disabled={disabled}
            allowClear
            notFoundContent={debounced ? (isFetching ? 'Поиск…' : 'Ничего не найдено') : null}
        />
    )
}

interface AddressSuggestProps {
    name: NamePath
    noAddressName: NamePath
    /** Полный путь от корня формы — для Form.useWatch */
    absoluteNoAddressPath: NamePath
    label?: ReactNode
    rules?: Rule[]
    placeholder?: string
    disabled?: boolean
    onSelectSuggestion?: (suggestion: Suggestion) => void
    onClearSuggestion?: () => void
    onNoAddressChange?: (checked: boolean) => void
}

const AddressSuggest: FC<AddressSuggestProps> = ({
    name,
    noAddressName,
    absoluteNoAddressPath,
    label,
    rules = [],
    placeholder = 'Начните вводить адрес',
    disabled,
    onSelectSuggestion,
    onClearSuggestion,
    onNoAddressChange,
}) => {
    const form = Form.useFormInstance()
    const noAddress = Form.useWatch(absoluteNoAddressPath, form) ?? false
    const confirmedRef = useRef<string | null>(null)

    const addressRules: Rule[] = [
        ...rules,
        {
            validator: async (_, value) => {
                if (form.getFieldValue(absoluteNoAddressPath)) {
                    return
                }

                const text = typeof value === 'string' ? value.trim() : ''
                if (!text) {
                    return Promise.reject(new Error('Укажите адрес или отметьте «Нет адреса»'))
                }

                if (text !== confirmedRef.current) {
                    return Promise.reject(new Error('Выберите адрес из подсказок'))
                }
            },
        },
    ]

    return (
        <Form.Item
            name={name}
            labelCol={{ span: 24 }}
            label={
                <Flex justify='space-between' align='center'>
                    {label}
                </Flex>
            }
            extra={
                <Form.Item name={noAddressName} valuePropName='checked' noStyle>
                    <Checkbox
                        disabled={disabled}
                        onChange={e => {
                            const checked = e.target.checked
                            if (checked) {
                                confirmedRef.current = null
                            }
                            onNoAddressChange?.(checked)
                        }}
                    >
                        Нет адреса
                    </Checkbox>
                </Form.Item>
            }
            dependencies={[noAddressName]}
            rules={addressRules}
            style={{ marginBottom: 8 }}
        >
            <AddressControl
                placeholder={placeholder}
                disabled={disabled || noAddress}
                onSelectSuggestion={onSelectSuggestion}
                onClearSuggestion={onClearSuggestion}
                confirmedRef={confirmedRef}
            />
        </Form.Item>
    )
}

export default AddressSuggest
